"use client";

import React, {useState} from "react";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "@/components/ui/select";
import {useRouter} from "next/navigation";
import {SliderRange} from "@/components/SliderRange";
import {formatPrice} from "@/utils/format-price";
import {ButtonGroup, ButtonGroupItem} from "@/components/CustomRadioGroup";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger,} from "@/components/ui/accordion";
import {BellRing, CarFront, Heart, Save, XCircle} from "lucide-react";
import {Badge} from "@/components/ui/badge";
import { Truck } from 'lucide-react'
import {Button} from "@/components/ui/button";
import {useSession} from "next-auth/react";

import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription} from "@/components/ui/dialog";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Separator} from "@/components/ui/separator";

export default function FilterMenu({
                                       filters,
                                        pageFrom,
                                       marcaList = [],
                                       modelloList = [],
                                       alimentazioneList = [],
                                       cambioList = [],
                                       minMaxPrezzo = [0, 100000],
                                       minMaxKm = [0, 200000],
                                       minMaxImmatricolazione = [2000, 2023],
                                   }: {
    pageFrom: string;
    filters: Record<string, string>;
    marcaList: Array<any>;
    modelloList: Array<any>;
    alimentazioneList: Array<any>;
    cambioList: Array<any>;
    minMaxPrezzo: [number, number];
    minMaxKm: [number, number];
    minMaxImmatricolazione: [number, number];
}) {
    const { data: session, status } = useSession();

    const router = useRouter();

    const [marca, setMarca] = useState(filters.marca || "");
    const [modello, setModello] = useState(filters.modello || "");
    const [alimentazione, setAlimentazione] = useState(filters.alimentazione || "");
    const [cambio, setCambio] = useState(filters.cambio || "");
    const [prezzoRange, setPrezzoRange] = useState([minMaxPrezzo[0], minMaxPrezzo[1]]);
    const [kmRange, setKmRange] = useState([minMaxKm[0], minMaxKm[1]]);
    const parsedMinMaxImmatricolazione = minMaxImmatricolazione.map(Number);
    const [immatricolazioneRange, setImmatricolazioneRange] = useState([
        parsedMinMaxImmatricolazione[0],
        parsedMinMaxImmatricolazione[1],
    ]);
    const [tipoVeicolo, setTipoVeicolo] = useState(filters.tipoVeicolo);
    // Stati per la funzionalità di salvataggio ricerca
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [searchName, setSearchName] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const handleFilterChange = (key: string, value: any) => {
        console.log(key, value);

        // Se la marca è selezionata, resetta tutti gli altri filtri
        if (key === "marca" && value === "none") {
            handleClearFilters();
            router.replace(`/${pageFrom}`);
            return;
        }

        let updatedFilters = {...filters};

        if (value == null || value === "none") {
            if (key === "kmRange") {
                const {kmDa, kmA, ...rest} = updatedFilters;
                updatedFilters = rest;
            } else if (key === "immatricolazioneRange") {
                const {annoDa, annoA, ...rest} = updatedFilters;
                updatedFilters = rest;
            } else if (key === "prezzoRange") {
                const {prezzoDa, prezzoA, ...rest} = updatedFilters;
                updatedFilters = rest;
            } else if (key === "tipoVeicolo") {
                const { tipoVeicolo, ...rest } = updatedFilters;
                updatedFilters = rest;
            } else {
                updatedFilters[key] = "";
            }
        } else {
            if (key === "kmRange") {
                const {[key]: _, ...rest} = updatedFilters;
                updatedFilters = {...rest, kmDa: value[0], kmA: value[1]};
            } else if (key === "immatricolazioneRange") {
                const {[key]: _, ...rest} = updatedFilters;
                updatedFilters = {...rest, annoDa: value[0], annoA: value[1]};
            } else if (key === "prezzoRange") {
                const {[key]: _, ...rest} = updatedFilters;
                updatedFilters = {...rest, prezzoDa: value[0], prezzoA: value[1]};
            } else {
                updatedFilters[key] = value;
            }
        }

        // Remove empty filters
        Object.keys(updatedFilters).forEach((key) => {
            if (updatedFilters[key] === "") {
                delete updatedFilters[key];
            }
        });

        const query = new URLSearchParams(updatedFilters).toString();
        router.push(`/${pageFrom}?${query}`);
    };

    const getStep = (valueRange: [number, number]) => {
        const maxValue = Math.max(...valueRange);
        if (maxValue <= 30000) {
            return 100;
        } else if (maxValue <= 70000) {
            return 1000;
        } else {
            return 2000;
        }
    };

    const handleSelectChange = (setter: Function, key: string) => (value: string) => {
        setter(value);
        handleFilterChange(key, value);
    };

    const handleSaveSearch = async () => {
        setIsSaving(true);

        try {
            // Prepara i filtri nel formato corretto per il backend
            const searchFilters = {
                marca: marca && marca !== "none" ? marca : undefined,
                modello: modello && modello !== "none" ? modello : undefined,
                alimentazione: alimentazione || undefined,
                cambio: cambio || undefined,
                tipoVeicolo: tipoVeicolo && tipoVeicolo !== "none" ? tipoVeicolo : undefined,
                prezzoMin: prezzoRange[0] !== minMaxPrezzo[0] ? prezzoRange[0] : undefined,
                prezzoMax: prezzoRange[1] !== minMaxPrezzo[1] ? prezzoRange[1] : undefined,
                kmMin: kmRange[0] !== minMaxKm[0] ? kmRange[0] : undefined,
                kmMax: kmRange[1] !== minMaxKm[1] ? kmRange[1] : undefined,
                annoMin: immatricolazioneRange[0] !== minMaxImmatricolazione[0] ? immatricolazioneRange[0] : undefined,
                annoMax: immatricolazioneRange[1] !== minMaxImmatricolazione[1] ? immatricolazioneRange[1] : undefined,
            };

            // Rimuovi i campi undefined
          (Object.keys(searchFilters) as Array<keyof typeof searchFilters>).forEach(key => {
              if (searchFilters[key] === undefined) {
                  delete searchFilters[key];
              }
          });

            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/account/ricerca`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.user?.token || ''}`,
                },
                body: JSON.stringify({
                    name: searchName.trim(),
                    filters: searchFilters,
                    filterShow: generateFilterShow()
                }),
            });

            const data = await response.json();

            if (response.ok && data.status === 'success') {
                setIsDialogOpen(false);
                setSearchName("");
            } else {
            }
        } catch (error) {
            console.error('Errore nel salvataggio della ricerca:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const generateFilterShow = () => {
        const filterLabels = [];

        if (marca && marca !== "none") {
            const marcaObj = marcaList.find(item => item.codice === marca);
            filterLabels.push({ label: `MARCA: ${marcaObj?.marca || marca}` });
        }

        if (modello && modello !== "none") {
            filterLabels.push({ label: `MODELLO: ${modello}` });
        }

        if (alimentazione) {
            filterLabels.push({ label: `ALIMENTAZIONE: ${alimentazione}` });
        }

        if (cambio) {
            filterLabels.push({ label: `CAMBIO: ${cambio}` });
        }

        if (tipoVeicolo && tipoVeicolo !== "none") {
            filterLabels.push({ label: `TIPO VEICOLO: ${tipoVeicolo}` });
        }

        if (prezzoRange[0] !== minMaxPrezzo[0] || prezzoRange[1] !== minMaxPrezzo[1]) {
            filterLabels.push({ label: `PREZZO: €${prezzoRange[0]} - €${prezzoRange[1]}` });
        }

        if (kmRange[0] !== minMaxKm[0] || kmRange[1] !== minMaxKm[1]) {
            filterLabels.push({ label: `KM: ${kmRange[0]} - ${kmRange[1]}` });
        }

        if (immatricolazioneRange[0] !== minMaxImmatricolazione[0] || immatricolazioneRange[1] !== minMaxImmatricolazione[1]) {
            filterLabels.push({ label: `ANNO: ${immatricolazioneRange[0]} - ${immatricolazioneRange[1]}` });
        }

        return filterLabels;
    };

    const generateSearchName = () => {
        const parts = [];

        if (marca && marca !== "none") {
            const marcaObj = marcaList.find(item => item.codice === marca);
            parts.push(marcaObj?.marca || marca);
        }

        if (modello && modello !== "none") {
            parts.push(modello);
        }

        if (alimentazione) {
            parts.push(alimentazione);
        }

        if (tipoVeicolo && tipoVeicolo !== "none") {
            parts.push(tipoVeicolo);
        }

        return parts.length > 0 ? parts.join(" ") : "Nuova ricerca";
    };

    const handleOpenSaveDialog = () => {
        setSearchName(generateSearchName());
        setIsDialogOpen(true);
    };

    // Verifica se ci sono filtri attivi
    const hasActiveFilters = () => {
        return (
            (marca && marca !== "none") ||
            (modello && modello !== "none") ||
            alimentazione ||
            cambio ||
            (tipoVeicolo && tipoVeicolo !== "none") ||
            prezzoRange[0] !== minMaxPrezzo[0] ||
            prezzoRange[1] !== minMaxPrezzo[1] ||
            kmRange[0] !== minMaxKm[0] ||
            kmRange[1] !== minMaxKm[1] ||
            immatricolazioneRange[0] !== minMaxImmatricolazione[0] ||
            immatricolazioneRange[1] !== minMaxImmatricolazione[1]
        );
    };


    const handleClearFilters = () => {
        setMarca("");
        setModello("");
        setAlimentazione("");
        setCambio("");
        setPrezzoRange([minMaxPrezzo[0], minMaxPrezzo[1]]);
        setKmRange([minMaxKm[0], minMaxKm[1]]);
        setImmatricolazioneRange([minMaxImmatricolazione[0], minMaxImmatricolazione[1]]);
        setTipoVeicolo("none");
        router.replace(`/${pageFrom}`);
    };

    return (

        <>
            <div className="bg-secondary rounded-lg ">
                <h4 className="font-bold text-3xl  p-4">Filtra per</h4>

                <div className="grid w-full grid-cols-2 gap-2 px-2">
                    <Button
                        key={`tipoVeicolo-auto-${tipoVeicolo}`}  // Key identificativa
                        variant={tipoVeicolo === "none" ? "default" : "outline"}
                        onClick={() => {
                            handleClearFilters();
                            setTipoVeicolo("none");
                            handleFilterChange("tipoVeicolo", "none");
                        }}                        className={`flex items-center justify-center gap-2 ${
                            tipoVeicolo === "none" ? "text-white" : ""
                        }`}
                    >
                        <CarFront className="inline-block"/> Auto
                    </Button>

                    <Button
                        key={`tipoVeicolo-furgone-${tipoVeicolo}`}  // Key identificativa
                        variant={tipoVeicolo === "Furgone" ? "default" : "outline"}
                        onClick={() => {
                            handleClearFilters();
                            setTipoVeicolo("Furgone");
                            handleFilterChange("tipoVeicolo", "Furgone");
                        }}                        className={`flex items-center justify-center gap-2 ${
                            tipoVeicolo === "Furgone" ? "text-white" : ""
                        }`}
                    >
                        <Truck className="inline-block"/> Commerciali
                    </Button>
                </div>


                <Accordion type="multiple" className="p-4">
                    {/*<AccordionItem value="tipo_veicolo" >
                        <AccordionTrigger className="pb-0">
                            Tipo di Veicolo
                        </AccordionTrigger>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {tipoVeicolo && tipoVeicolo !== "all" && (
                                <Badge variant="outline" className="text-xs px-3 py-1">
                                    {tipoVeicolo === "commerciale" ? "Veicoli Commerciali" : "Tutti i Veicoli"}
                                </Badge>
                            )}
                        </div>
                        <AccordionContent className="flex flex-col gap-y-3">
                            <Select value={tipoVeicolo} onValueChange={handleSelectChange(setTipoVeicolo, "tipoVeicolo")}>
                                <SelectTrigger id="tipoVeicolo__select">
                                    <SelectValue placeholder="Seleziona Tipo di Veicolo" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti i Veicoli</SelectItem>
                                    <SelectItem value="commerciale">Veicoli Commerciali</SelectItem>
                                </SelectContent>
                            </Select>

                            <div className="flex items-center space-x-2">
                                <Switch id="airplane-mode"/>
                                <Label htmlFor="airplane-mode">Mostra solo Veicoli Commerciali</Label>
                            </div>
                        </AccordionContent>
                    </AccordionItem>*/}
                    <AccordionItem value="marca">
                        <AccordionTrigger className="pb-0">
                            Marca e modello
                        </AccordionTrigger>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {marca && marca !== "none" && (
                                <Badge variant="outline" className="text-xs px-3 py-1">
                                    {marcaList.find((item) => item.codice === marca)?.marca || marca}
                                </Badge>
                            )}
                            {modello && modello !== "none" && (
                                <Badge variant="outline" className="text-xs px-3 py-1">
                                    {modello}
                                </Badge>
                            )}
                        </div>
                        <AccordionContent className="flex flex-col gap-y-3">
                            <Select value={marca} onValueChange={handleSelectChange(setMarca, "marca")}>
                                <SelectTrigger id="marca__select">
                                    <SelectValue placeholder="Marca"/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Tutte le Marche</SelectItem>
                                    {marcaList.filter((item) => item.marca && item.codice).map((item, index) => (
                                        <SelectItem key={index} value={item.codice || `unknown-marca-${index}`}>
                                            {item.marca || "Marca sconosciuta"} ({item.count || 0})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select value={modello} onValueChange={handleSelectChange(setModello, "modello")}>
                                <SelectTrigger id="modello__select">
                                    <SelectValue placeholder="Modello"/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Tutti i Modelli</SelectItem>
                                    {modelloList.filter((item) => item.modello).map((item, index) => (
                                        <SelectItem key={index} value={item.modello || `unknown-modello-${index}`}>
                                            {item.modello || "Modello sconosciuto"} ({item.count || 0})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="alimentazione">
                        <AccordionTrigger className="pb-0">Alimentazione</AccordionTrigger>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {alimentazione && (
                                <Badge variant="outline" className="text-xs px-3 py-1">
                                    {alimentazione}
                                </Badge>
                            )}
                        </div>
                        <AccordionContent>
                            <ButtonGroup
                                wrap={true}
                                className="gap-2"
                                value={alimentazione}
                                allowDeselect={true}
                                onValueChange={(value) => {
                                    setAlimentazione(value || "");
                                    handleFilterChange("alimentazione", value);
                                }}
                            >
                                {alimentazioneList.filter((item) => item.alimentazione).map((item, index) => (
                                    <ButtonGroupItem
                                        key={index}
                                        showCheck={false}
                                        className="justify-between px-3"
                                        value={item.alimentazione}
                                        label={`${item.alimentazione} `}
                                    />
                                ))}
                            </ButtonGroup>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="cambio">
                        <AccordionTrigger className="pb-0">Cambio</AccordionTrigger>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {cambio && (
                                <Badge variant="outline" className="text-xs px-3 py-1">
                                    {cambio.charAt(0).toUpperCase() + cambio.slice(1).toLowerCase()}
                                </Badge>
                            )}
                        </div>
                        <AccordionContent>
                            <ButtonGroup
                                wrap={true}
                                className="gap-2"
                                value={cambio}
                                allowDeselect={true}
                                onValueChange={(value) => {
                                    setCambio(value || "");
                                    handleFilterChange("cambio", value);
                                }}
                            >
                                {cambioList.filter((item) => item.cambio && item.cambio != 'ALTRO').map((item, index) => (
                                    <ButtonGroupItem
                                        key={index}
                                        showCheck={false}
                                        className="justify-between px-3"
                                        value={item.cambio}
                                        label={`${item.cambio.charAt(0).toUpperCase() + item.cambio.slice(1).toLowerCase()} `}
                                    />
                                ))}
                            </ButtonGroup>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="immatricolazione">
                        <AccordionTrigger className="pb-0">Immatricolazione</AccordionTrigger>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {!(immatricolazioneRange[0] === minMaxImmatricolazione[0] && immatricolazioneRange[1] === minMaxImmatricolazione[1]) && (
                                <Badge variant="outline" className="text-xs px-3 py-1">
                                    Immatricolazione {immatricolazioneRange[0]} - {immatricolazioneRange[1]}
                                </Badge>
                            )}
                        </div>

                        <AccordionContent>
                            <div className="flex flex-col my-3">
                                <div className="flex justify-between my-2">
                                    <p className="font-medium">Dal {parseInt(String(immatricolazioneRange[0]))}</p>
                                    <p className="font-medium">al {parseInt(String(immatricolazioneRange[1]))}</p>
                                </div>
                                <SliderRange
                                    min={parsedMinMaxImmatricolazione[0]}
                                    max={parsedMinMaxImmatricolazione[1]}
                                    step={1}
                                    value={immatricolazioneRange}
                                    onValueCommit={(value) => {
                                        handleFilterChange("immatricolazioneRange", value);
                                    }}
                                    onValueChange={(value) => {
                                        setImmatricolazioneRange(value);
                                        parsedMinMaxImmatricolazione[0] = value[0];
                                        parsedMinMaxImmatricolazione[1] = value[1];
                                    }}
                                />
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="Chilometri">
                        <AccordionTrigger className="pb-0">Chilometri</AccordionTrigger>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {!(kmRange[0] === minMaxKm[0] && kmRange[1] === minMaxKm[1]) && (
                                <Badge variant="outline" className="text-xs px-3 py-1">
                                    {parseInt(String(kmRange[0]))} - {parseInt(String(kmRange[1]))} Km
                                </Badge>
                            )}
                        </div>
                        <AccordionContent>
                            <div className="flex flex-col my-3">
                                <div className="flex justify-between my-2">
                                    <p className="font-medium">Da {parseInt(String(kmRange[0]))} Km</p>
                                    <p className="font-medium">a {parseInt(String(kmRange[1]))} Km</p>
                                </div>
                                <SliderRange
                                    min={minMaxKm[0]}
                                    max={minMaxKm[1]}
                                    step={getStep(minMaxKm)}
                                    value={kmRange}
                                    onValueCommit={(value) => {
                                        handleFilterChange("kmRange", value);
                                    }}
                                    onValueChange={(value) => {
                                        setKmRange(value);
                                    }}
                                    formatLabel={(value) => `${formatPrice(value)}`}
                                />
                            </div>
                        </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="prezzo">
                        <AccordionTrigger className="pb-0">Prezzo</AccordionTrigger>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {!(prezzoRange[0] === minMaxPrezzo[0] && prezzoRange[1] === minMaxPrezzo[1]) && (
                                <Badge variant="outline" className="text-xs px-3 py-1">
                                    Prezzo {formatPrice(prezzoRange[0])} - {formatPrice(prezzoRange[1])}
                                </Badge>
                            )}
                        </div>
                        <AccordionContent>
                            <div className="flex flex-col my-3">
                                <div className="flex justify-between my-2">
                                    <p className="font-medium">Da {formatPrice(prezzoRange[0])}</p>
                                    <p className="font-medium">a {formatPrice(prezzoRange[1])}</p>
                                </div>
                                <SliderRange
                                    min={minMaxPrezzo[0]}
                                    max={minMaxPrezzo[1]}
                                    step={getStep(minMaxPrezzo)}
                                    value={prezzoRange}
                                    onValueCommit={(value) => {
                                        handleFilterChange("prezzoRange", value);
                                    }}
                                    onValueChange={(value) => {
                                        setPrezzoRange(value);
                                    }}
                                />
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>

            {session && hasActiveFilters() && (
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button variant="outline" onClick={handleOpenSaveDialog} className="w-full mt-4 mb-2 flex items-center justify-center gap-2">
                            <BellRing className="w-4 h-4 mr-2"/> Salva ricerca
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                            <DialogTitle>Salva la tua ricerca</DialogTitle>
                            <DialogDescription>
                                Salva questa ricerca per ricevere notifiche quando ci sono nuovi veicoli che corrispondono ai tuoi criteri.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                            <div className="flex flex-col gap-y-2">
                                <Label htmlFor="search-name" className="text-left">
                                    Dai un nome alla tua ricerca
                                </Label>
                                <Input
                                    id="search-name"
                                    value={searchName}
                                    onChange={(e) => setSearchName(e.target.value)}
                                    className="w-full"
                                    placeholder="Es. BMW Serie 3 Diesel"
                                    maxLength={50}
                                />
                            </div>

                            {/* Preview dei filtri attivi */}
                            <div className="flex flex-col gap-y-2">
                                <Label className="text-left text-sm text-muted-foreground">
                                    Filtri
                                </Label>
                                <div className="col-span-3 flex flex-wrap gap-1">
                                    {marca && marca !== "none" && (
                                        <Badge variant="secondary" className="text-xs">
                                            {marcaList.find(item => item.codice === marca)?.marca || marca}
                                        </Badge>
                                    )}
                                    {modello && modello !== "none" && (
                                        <Badge variant="secondary" className="text-xs">
                                            {modello}
                                        </Badge>
                                    )}
                                    {alimentazione && (
                                        <Badge variant="secondary" className="text-xs">
                                            {alimentazione}
                                        </Badge>
                                    )}
                                    {cambio && (
                                        <Badge variant="secondary" className="text-xs">
                                            {cambio}
                                        </Badge>
                                    )}
                                    {tipoVeicolo && tipoVeicolo !== "none" && (
                                        <Badge variant="secondary" className="text-xs">
                                            {tipoVeicolo}
                                        </Badge>
                                    )}
                                    {(prezzoRange[0] !== minMaxPrezzo[0] || prezzoRange[1] !== minMaxPrezzo[1]) && (
                                        <Badge variant="secondary" className="text-xs">
                                            {formatPrice(prezzoRange[0])} - {formatPrice(prezzoRange[1])}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsDialogOpen(false)}
                            >
                                Annulla
                            </Button>
                            <Button
                                type="button"
                                onClick={handleSaveSearch}
                                disabled={isSaving || !searchName.trim()}
                                className="flex items-center gap-2 text-white"
                            >
                                {isSaving ? (
                                    <>
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                        Salvataggio...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4" />
                                        Salva ricerca
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}


            <button onClick={handleClearFilters}
                    className="bg-transparent flex flex-row justify-center gap-x-2 py-3 w-full items-center text-centerself-start">
                <XCircle className="w-4 h-4"/>
                Cancella filtri
            </button>
        </>

    );
}
